import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Container,
  Typography,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import useQuery from '../../hooks/useQuery';
import { useApi } from '../../hooks/useApi';
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';

const BRAND = '#032C65';
const BRAND_LIGHT = '#f0f4ff';

function AnnouncementCard({ announcement, onEdit, onDelete }) {
  const { t } = useTranslation();
  const { hashid, title, body, picture, active } = announcement;
  const expiresAt = announcement.expires_at;

  const isExpired = expiresAt && dayjs(expiresAt).isBefore(dayjs());
  const effectivelyActive = active && !isExpired;

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid',
        borderColor: effectivelyActive ? 'rgba(3,44,101,0.12)' : 'rgba(0,0,0,0.08)',
        boxShadow: effectivelyActive
          ? '0 2px 12px rgba(3,44,101,0.08)'
          : '0 1px 4px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: effectivelyActive ? 1 : 0.65,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: '0 6px 24px rgba(3,44,101,0.13)', transform: 'translateY(-2px)' },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          height: 160,
          background: picture ? `url(${picture}) center/cover no-repeat` : BRAND_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {!picture && <CampaignIcon sx={{ fontSize: 56, color: BRAND, opacity: 0.18 }} />}

        {/* Status badge */}
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          {isExpired && <Chip label={t('expired')} color="error" size="small" sx={{ fontWeight: 700, fontSize: 11 }} />}
          {!isExpired && active && <Chip label={t('active')} color="success" size="small" sx={{ fontWeight: 700, fontSize: 11, background: '#16a34a', color: '#fff' }} />}
          {!isExpired && !active && <Chip label={t('inactive') || 'Inactivo'} size="small" sx={{ fontWeight: 700, fontSize: 11, background: '#6b7280', color: '#fff' }} />}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color={BRAND}
          sx={{ lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {title}
        </Typography>

        {body && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}
          >
            {body}
          </Typography>
        )}

        {expiresAt && (
          <Stack direction="row" alignItems="center" spacing={0.5} mt="auto" pt={1}>
            <AccessTimeIcon sx={{ fontSize: 14, color: isExpired ? 'error.main' : 'warning.main' }} />
            <Typography variant="caption" color={isExpired ? 'error.main' : 'warning.dark'} fontWeight={600}>
              {isExpired ? t('expired') : `${t('expires')}: ${dayjs(expiresAt).format('MMM D, YYYY')}`}
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Footer actions */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 0.5,
          background: '#fafbff',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {(() => {
            let iconColor = 'text.disabled';
            let textColor = 'text.disabled';
            let label = t('no_expiry') || 'Sin expiración';
            if (expiresAt && isExpired) { iconColor = 'error.main'; textColor = 'error.main'; label = t('expired'); }
            else if (expiresAt) { iconColor = 'warning.main'; textColor = 'warning.dark'; label = dayjs(expiresAt).format('MMM D, YYYY'); }
            return (
              <>
                <AccessTimeIcon sx={{ fontSize: 13, color: iconColor }} />
                <Typography variant="caption" color={textColor} fontWeight={600}>{label}</Typography>
              </>
            );
          })()}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={t('edit')}>
            <IconButton size="small" onClick={() => onEdit(hashid)} sx={{ color: BRAND }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <IconButton size="small" onClick={() => onDelete(hashid)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
}

export default function AnnouncementIndex() {
  const [announcements, setAnnouncements] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 12, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort] = useState('desc');
  const [sortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHashid, setSelectedHashid] = useState(null);

  const fetchData = useQuery();
  const { doDelete } = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('announcements', filter, sort, setAnnouncements, sortField, setPagination, page, () => {});
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField]);

  const handleDelete = () => {
    doDelete(`announcements/${selectedHashid}`, t('delete_success'), t('delete_error'), refreshTable);
    setShowDeleteDialog(false);
  };

  return (
    <Page title={t('announcements')}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight={800} color={BRAND}>
            {t('announcements')}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/announcement/save"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ background: BRAND, '&:hover': { background: '#054a9e' }, borderRadius: 2, px: 3 }}
          >
            {t('new_announcement')}
          </Button>
        </Stack>

        {/* Search bar + content */}
        <Card>
          <Box p={3} pb={0}>
          <TextField
            size="small"
            placeholder={t('search') || 'Buscar...'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: 340 }, background: '#fff', borderRadius: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          </Box>

          <Box p={3}>
        {/* Cards grid */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <FadeLoader color={BRAND} loading />
          </Box>
        )}
        {!isLoading && announcements.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              borderRadius: 3,
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <CampaignIcon sx={{ fontSize: 64, color: BRAND, opacity: 0.15, mb: 2 }} />
            <Typography color="text.secondary">{t('no_announcements')}</Typography>
          </Box>
        )}
        {!isLoading && announcements.length > 0 && (
          <Grid container spacing={3}>
            {announcements.map((ann) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={ann.hashid}>
                <AnnouncementCard
                  announcement={ann}
                  onEdit={(hid) => navigate(`/dashboard/announcement/edit/${hid}`)}
                  onDelete={(hid) => { setSelectedHashid(hid); setShowDeleteDialog(true); }}
                />
              </Grid>
            ))}
          </Grid>
        )}
          </Box>

        {/* Pagination */}
        {pagination.total > 0 && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 12}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
        )}

        <ConfirmationDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          message={t('delete_confirm')}
        />
        </Card>
      </Container>
    </Page>
  );
}


